import { useEffect, useState } from "react";
import { Inbox, CheckCircle2, XCircle, Clock, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading } from "@/pages/_shared";
import api from "@/lib/api";

const STATUS_STYLE = {
  PENDING: { c: "#a16207", i: Clock }, APPROVED: { c: "#059669", i: CheckCircle2 }, DECLINED: { c: "#dc2626", i: XCircle },
};

export default function ApprovalInbox() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("PENDING");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/approvals"); setItems(data.items); } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/approvals/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — action executed (SANDBOX) and logged to audit" : "Declined and logged to audit");
      await load();
    } catch (e) { toast.error("Could not update approval"); } finally { setBusy(null); }
  };

  if (!items) return <Loading />;
  const shown = tab === "ALL" ? items : items.filter((i) => i.status === tab);
  const pending = items.filter((i) => i.status === "PENDING").length;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="approval-inbox-page">
      <PageHeader eyebrow="Human-in-the-loop" title="Approval Inbox"
        subtitle="Agents can prepare work, but sensitive actions are deterministic and require your explicit approval. Every decision is written to the audit trail."
        badge={<StatusChip state="LIVE" />} />

      <div className="flex gap-2">
        {["PENDING", "APPROVED", "DECLINED", "ALL"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`approval-tab-${t.toLowerCase()}`}
            className={`rounded-lg px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider transition-colors ${tab === t ? "bg-[#080d1a] text-white" : "border border-slate-200 bg-white text-slate-500"}`}>
            {t}{t === "PENDING" && pending > 0 ? ` (${pending})` : ""}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {shown.length === 0 && <div className="py-12 text-center text-sm text-slate-400">No {tab.toLowerCase()} approvals.</div>}
        {shown.map((a) => {
          const st = STATUS_STYLE[a.status] || STATUS_STYLE.PENDING;
          const Icon = st.i;
          return (
            <Card key={a.id} className="p-5" data-testid="approval-item">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100"><ShieldAlert size={17} className="text-[#1a5fd0]" /></div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-head text-[15px] font-bold text-slate-900">{a.title}</div>
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider" style={{ color: st.c }}>
                      <Icon size={12} /> {a.status}
                    </span>
                  </div>
                  <p className="mt-1 text-[13px] text-slate-500">{a.description}</p>
                  {(a.recipient || a.data_shared || a.consequence) && (
                    <div className="mt-2 grid gap-1.5 rounded-lg bg-slate-50 px-3 py-2 sm:grid-cols-3" data-testid="approval-integrity">
                      <div><div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Recipient / System</div><div className="text-[12px] text-slate-700">{a.recipient}</div></div>
                      <div><div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Data shared</div><div className="text-[12px] text-slate-700">{a.data_shared}</div></div>
                      <div><div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Consequence</div><div className="text-[12px] text-slate-700">{a.consequence}</div></div>
                    </div>
                  )}
                  {a.payload_hash && (
                    <div className="mt-1.5 font-mono text-[10px] text-slate-400" data-testid="approval-hash">integrity ••{a.payload_hash.slice(-12)} — approval binds to this exact payload</div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-400">Requested by {a.requested_by}</span>
                    <StatusChip state={a.data_class === "RESTRICTED IDENTITY" ? "MISMATCH" : "SANDBOX"} />
                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">exec: {a.execution_state}</span>
                    <span className="rounded-md px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-500">{a.data_class}</span>
                  </div>
                  {a.status === "PENDING" && (
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => decide(a.id, "approve")} disabled={busy === a.id} data-testid="approval-approve-btn"
                        className="flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3.5 py-2 text-[12px] font-semibold text-white disabled:opacity-50">
                        {busy === a.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Approve
                      </button>
                      <button onClick={() => decide(a.id, "decline")} disabled={busy === a.id} data-testid="approval-decline-btn"
                        className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-[12px] font-semibold text-slate-600 disabled:opacity-50">
                        <XCircle size={13} /> Decline
                      </button>
                    </div>
                  )}
                  {a.decided_by && <div className="mt-2 font-mono text-[10px] text-slate-400">Decided by {a.decided_by}</div>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
